/** @format */

import { Amplify, type ResourcesConfig } from "aws-amplify";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Routes, Route, useNavigate } from "react-router-dom";
import {
	SignUp,
	Home,
	SignIn,
	VerifyAndConfirmAccount,
	UserProfile,
	PasswordResetModal,
	CircleLeaderApplication,
	ForgotPasswordForm,
	CreateCircle,
	LeaderCircle,
	PageNotFound,
	AllCirclesView,
	AddRequestModal,
	CircleRouteWrapper,
	JoinRequestsOfCircle,
	IdentityVerifiedPage,
	TermsOfServices,
	PrivacyPolicy,
	SmsOptInEvidence,
	CollectSmsConsent,
	CookiePolicy,
	Disclosure,
	AboutUs,
	Features,
	FAQPage,
	AccountLink,
	AccountLinkReturnPage,
	AdminDashboard,
	CircleLeaderApplications,
	AllUsers,
	AccountDeleted,
	AccountBanned,
	AllCirclesAdminView,
	JoinRequestApplications,
	AddRequestsApplications,
	SavingsCircle,
	GroupContributions,
	FinancialPlanning,
	SecurePayments,
	CommunitySupport,
	AllUsersAutoDraft,
	AutoDraftReports,
	ContributionReports,
	PayOutReports,
	Fees,
	RevenueReports,
	ViewCircleInvitations,
	InvitationApprovals,
	AdminAllInvitations,
	StripeSupportedCountries,
	SandboxDemoPage,
	SandboxSharedPage,
	FeedbackPage,
	BusinessSurveyPage,
	BusinessLandingPage,
	BusinessPricingPage,
	CollectLayout,
	CollectDashboard,
	CollectAnalytics,
	CollectSetup,
	CollectConnectQuickBooks,
	CollectInvoices,
	CollectInvoiceDetail,
	CollectCustomers,
	CollectCustomerDetail,
	CollectRecoveryQueue,
	CollectPromises,
	CollectReplies,
	CollectTasks,
	CollectManualReview,
	CollectLegalAction,
	CollectCampaigns,
	CollectTemplates,
	CollectActivity,
	CollectRecommendations,
	CollectPredictions,
	CollectSettings,
	CollectSubscribe,
	CollectBilling,
	CollectAdminDashboard,
} from "./components/allComponents/AllComponents";
import { ConditionalNavbar } from "./components/navbar/ConditionalNavbar";
import { Provider, useDispatch, useSelector } from "react-redux";
import { AppDispatch, persistor, store } from "./redux/store/store";
import { PersistGate } from "redux-persist/integration/react";
import { useCallback, useEffect, useState } from "react";
import { checkTokenValidity } from "./auth/tokenAuth";
import { setAuthenticationState } from "./redux/reducers/userReducer";
import ThemeProvider from "./theme";
import { ProtectedRoute } from "./components/protectedRoutes/ProtectedRoutes";
import { BusinessProtectedRoute } from "./components/protectedRoutes/BusinessProtectedRoute";
import BusinessSignUp from "./components/collect/auth/BusinessSignUp";
import BusinessTeamSignUp from "./components/collect/auth/BusinessTeamSignUp";
import BusinessVerify from "./components/collect/auth/BusinessVerify";
import BusinessSignIn from "./components/collect/auth/BusinessSignIn";
import BusinessForgotPassword from "./components/collect/auth/BusinessForgotPassword";
import CollectTeam from "./components/collect/CollectTeam";
import CollectSupport from "./components/collect/CollectSupport";
import CollectSupportTicketDetail from "./components/collect/CollectSupportTicketDetail";
import CollectSupportQueue from "./components/collect/admin/CollectSupportQueue";
import CollectSupportTicketAdmin from "./components/collect/admin/CollectSupportTicketAdmin";
import HostedInvoicePay from "./components/hosted-pay/HostedInvoicePay";
import { ToastBridge, ToastProvider } from "./providers/ToastProvider";
import { RoleNames } from "./utility/types/rolesType";
import { getAllReviews } from "./redux/actions/reviewAction";
import { setupAxiosInterceptors } from "./axiosConfig/axiosInterceptor";
import { Navigate } from "react-router-dom";
import {
	selectCurrentUser,
	selectIsAuthenticated,
} from "./redux/selectors/userSelector";
import { getUnauthRedirectPath } from "./utility/redirectHelper";

// Stripe publishable key → a single, module-scoped promise. Stripe requires the
// `stripe` prop passed to <Elements> to keep a STABLE identity across renders;
// loadStripe() already returns one cached promise, so we hand it over directly
// (no extra .then/.catch wrapper, which would create a new promise identity on
// Fast Refresh and risk an unhandled rejection).
const stripePromise = loadStripe(
	process.env.REACT_APP_STRIPE_LIVE_MODE_PUBLIC_KEY ?? "",
);

const authConfig: ResourcesConfig["Auth"] = {
	Cognito: {
		userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID as string,
		userPoolClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID as string,
	},
};

Amplify.configure({
	Auth: authConfig,
});

/**
 * If a returning user (has persisted userData from a previous session)
 * visits "/", redirect them to /circle/sign-in instead of showing the landing page.
 * Already-authenticated users are also sent to /circle/sign-in (or you could send
 * them to their profile instead).
 * Brand-new visitors with no account see the normal Home page.
 */
function HomeOrRedirect() {
	const currentUser = useSelector(selectCurrentUser);
	const isAuthenticated = useSelector(selectIsAuthenticated);

	// User has an account (persisted data) but is not logged in → sign-in
	if (currentUser && !isAuthenticated) {
		return <Navigate to="/circle/sign-in" replace />;
	}

	// User is already authenticated → you could redirect to profile/circles
	// Uncomment the line below if you want that behaviour:
	// if (isAuthenticated) return <Navigate to="/circles" replace />;

	return <BusinessLandingPage />

	// return <Home />;
}

function App() {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();

	const handleUnAuthenticated = useCallback(() => {
		dispatch(setAuthenticationState(false));
		navigate(getUnauthRedirectPath());
	}, [dispatch, navigate]);

	const onTokenValid = useCallback(() => {
		dispatch(setAuthenticationState(true));
	}, [dispatch]);

	// Setup axios interceptors for global error handling
	useEffect(() => {
		setupAxiosInterceptors(navigate);
	}, [navigate]);

	useEffect(() => {
		dispatch(getAllReviews());
	}, [dispatch]);

	// State to track whether token validity check is active
	const [tokenValidityCheckActive, setTokenValidityCheckActive] =
		useState(true);

	// will run when the user signIn/signOut
	// making sure to clear the user state and cookies
	// also checks for token validity every 5 minutes
	useEffect(() => {
		let tokenValidityInterval: any;

		// Token validity check
		const checkToken = () => {
			dispatch(checkTokenValidity(onTokenValid));
		};

		// Initial token validity check
		checkToken();

		// Set interval check for token validity
		tokenValidityInterval = setInterval(checkToken, 30 * 60 * 1000);

		// Clean up function to clear interval and handle un-authentication
		return () => {
			if (tokenValidityInterval) {
				clearInterval(tokenValidityInterval);
			}
			if (!tokenValidityCheckActive) {
				handleUnAuthenticated();
			}
		};
	}, [dispatch, tokenValidityCheckActive, handleUnAuthenticated, onTokenValid]);

	// Handle un-authentication when the component mounts
	useEffect(() => {
		if (!localStorage.getItem("recentlyLoggedOut")) {
			setTokenValidityCheckActive(true);
		} else {
			setTokenValidityCheckActive(false);
		}
	}, []);

	return (
		<Elements stripe={stripePromise}>
			<ThemeProvider>
				<Provider store={store}>
					<PersistGate loading={null} persistor={persistor}>
						<ToastProvider>
							<ToastBridge />
							<div className="App">
								<ConditionalNavbar />

								<Routes>
									<Route path="/" element={<HomeOrRedirect />} />
									<Route path="/terms-of-service" element={<TermsOfServices />} />
									<Route path="/privacy-policy" element={<PrivacyPolicy />} />
									<Route path="/sms-opt-in-evidence" element={<SmsOptInEvidence />} />
									<Route path="/collect-sms-consent" element={<CollectSmsConsent />} />
									<Route path="/cookies" element={<CookiePolicy />} />
									<Route path="disclosure" element={<Disclosure />} />
									<Route path="/about-us" element={<AboutUs />} />
									<Route path="/features" element={<Features />} />
									<Route path="/faq" element={<FAQPage />} />
									<Route path="/supported-countries" element={<StripeSupportedCountries />} />
									<Route path="/fees" element={<Fees />} />
									<Route path="/feedback" element={<FeedbackPage />} />
									<Route path="/business" element={<BusinessLandingPage />} />
									<Route path="/business/pricing" element={<BusinessPricingPage />} />
									<Route path="/circle" element={<Home />} />
									<Route path="/business/survey" element={<BusinessSurveyPage />} />
									<Route path="/circle/sign-up" element={<SignUp />} />							{/* ── Sandbox routes (public, no auth required) ── */}
									<Route path="/sandbox/demo" element={<SandboxDemoPage />} />
									{/* ── Hosted invoice pay page (public, opaque token in URL) ── */}
									<Route path="/pay/:payToken" element={<HostedInvoicePay />} />
									{/* ── Collect business auth (public) ── */}
									<Route path="/collect/sign-up" element={<BusinessSignUp />} />
									<Route
										path="/collect/team-sign-up"
										element={<BusinessTeamSignUp />}
									/>
									<Route path="/collect/verify" element={<BusinessVerify />} />
									<Route path="/collect/sign-in" element={<BusinessSignIn />} />
									<Route
										path="/collect/forgot-password"
										element={<BusinessForgotPassword />}
									/>
									{/* ── Collect onboarding (full-screen, no side panel) ── */}
									<Route
										path="/collect/setup"
										element={<BusinessProtectedRoute component={CollectSetup} />}
									/>
									<Route
										path="/collect/connect"
										element={
											<BusinessProtectedRoute
												component={CollectConnectQuickBooks}
											/>
										}
									/>
									{/* ── Collect workspace (side-panel layout shell) ── */}
									<Route
										path="/collect"
										element={<BusinessProtectedRoute component={CollectLayout} />}
									>
										<Route index element={<CollectDashboard />} />
										<Route path="dashboard" element={<CollectDashboard />} />
										<Route path="analytics" element={<CollectAnalytics />} />
										<Route path="invoices" element={<CollectInvoices />} />
										<Route
											path="invoices/:invoiceId"
											element={<CollectInvoiceDetail />}
										/>
										<Route path="customers" element={<CollectCustomers />} />
										<Route
											path="customers/:customerId"
											element={<CollectCustomerDetail />}
										/>
										<Route path="recovery" element={<CollectRecoveryQueue />} />
										<Route path="promises" element={<CollectPromises />} />
										<Route path="replies" element={<CollectReplies />} />
										<Route path="tasks" element={<CollectTasks />} />
										<Route path="review" element={<CollectManualReview />} />
										<Route path="legal-action" element={<CollectLegalAction />} />
										<Route path="campaigns" element={<CollectCampaigns />} />
										<Route path="templates" element={<CollectTemplates />} />
										<Route path="team" element={<CollectTeam />} />
										<Route
											path="recommendations"
											element={<CollectRecommendations />}
										/>
										<Route
											path="predictions"
											element={<CollectPredictions />}
										/>
										<Route path="activity" element={<CollectActivity />} />
										<Route path="settings" element={<CollectSettings />} />
										<Route path="support" element={<CollectSupport />} />
										<Route
											path="support/:ticketId"
											element={<CollectSupportTicketDetail />}
										/>
										<Route path="billing" element={<CollectBilling />} />
										<Route path="subscribe" element={<CollectSubscribe />} />
										<Route path="admin" element={<CollectAdminDashboard />} />
										<Route
											path="admin/support"
											element={<CollectSupportQueue />}
										/>
										<Route
											path="admin/support/:ticketId"
											element={<CollectSupportTicketAdmin />}
										/>
										{/* Unknown Collect sub-paths stay inside Collect
										    instead of falling through to the Circle 404/sign-in */}
										<Route
											path="*"
											element={<Navigate to="/collect" replace />}
										/>
									</Route>
									<Route
										path="/sandbox/share/:shareToken"
										element={<SandboxSharedPage />}
									/>								<Route path="/circle/sign-in" element={<SignIn />} />
									<Route path="/account-deleted" element={<AccountDeleted />} />
									<Route path="/account-banned" element={<AccountBanned />} />
									<Route
										path="/products/savings-circles"
										element={<SavingsCircle />}
									/>
									<Route
										path="/products/group-contributions"
										element={<GroupContributions />}
									/>
									<Route
										path="/products/financial-planning"
										element={<FinancialPlanning />}
									/>
									<Route
										path="/products/secure-payments"
										element={<SecurePayments />}
									/>
									<Route
										path="/products/community-support"
										element={<CommunitySupport />}
									/>
									<Route
										path="/identity-verified"
										element={<IdentityVerifiedPage />}
									/>
									<Route
										path="/account-link/onboarding/refresh/:userId"
										element={<AccountLink />}
									/>
									<Route
										path="/account-link/onboarding/return/:userId"
										element={<AccountLinkReturnPage />}
									/>
									<Route
										path="/user/password-reset"
										element={<ProtectedRoute component={PasswordResetModal} />}
									/>
									<Route
										path="/user/forgot-password"
										element={<ForgotPasswordForm />}
									/>
									<Route path="/verify" element={<VerifyAndConfirmAccount />} />
									<Route
										path="/user/:userId/*"
										element={<ProtectedRoute component={UserProfile} />}
									/>
									<Route
										path="/applications/leader"
										element={
											<ProtectedRoute component={CircleLeaderApplication} />
										}
									/>
									<Route
										path="/circle-create"
										element={
											<ProtectedRoute
												component={CreateCircle}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>
									<Route
										path="/my-circles"
										element={
											<ProtectedRoute
												component={LeaderCircle}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>
									<Route
										path="/join-requests"
										element={
											<ProtectedRoute
												component={JoinRequestsOfCircle}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>
									<Route
										path="/admin/view-all/invitations"
										element={
											<ProtectedRoute
												component={AdminAllInvitations}
												requiredRoles={[RoleNames.ADMIN, RoleNames.SUPER_ADMIN]}
											/>
										}
									/>
									<Route
										path="/invitation-approvals"
										element={
											<ProtectedRoute
												component={InvitationApprovals}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>
									<Route
										path="/add-request"
										element={<ProtectedRoute component={AddRequestModal} />}
									/>
									<Route
										path="/circle/:circleId"
										element={<CircleRouteWrapper />}
									/>
									<Route
										path="/circles"
										element={<ProtectedRoute component={AllCirclesView} />}
									/>
									<Route
										path="/view-invitation"
										element={<ProtectedRoute component={ViewCircleInvitations} />}
									/>
									<Route
										path="/invitations/accept"
										element={<ProtectedRoute component={ViewCircleInvitations} />}
									/>
									<Route
										path="/invitations/deny"
										element={<ProtectedRoute component={ViewCircleInvitations} />}
									/>

									<Route
										path="/admin/dashboard"
										element={
											<ProtectedRoute
												component={AdminDashboard}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>
									<Route
										path="/admin/circle-leader/applications"
										element={
											<ProtectedRoute
												component={CircleLeaderApplications}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>
									<Route
										path="/admin/view-all/users"
										element={
											<ProtectedRoute
												component={AllUsers}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>
									<Route
										path="/admin/view-all/users/auto-draft"
										element={
											<ProtectedRoute
												component={AllUsersAutoDraft}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>
									<Route
										path="/admin/view-all/auto-draft/reports"
										element={
											<ProtectedRoute
												component={AutoDraftReports}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>

									<Route
										path="/admin/view-all/contribution/reports"
										element={
											<ProtectedRoute
												component={ContributionReports}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>

									<Route
										path="/admin/view-all/payout/reports"
										element={
											<ProtectedRoute
												component={PayOutReports}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>

									<Route
										path="/admin/view-all/revenue/reports"
										element={
											<ProtectedRoute
												component={RevenueReports}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>

									<Route
										path="/admin/view-all/circles"
										element={
											<ProtectedRoute
												component={AllCirclesAdminView}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>
									<Route
										path="/admin/view-all/join-requests"
										element={
											<ProtectedRoute
												component={JoinRequestApplications}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>
									<Route
										path="/admin/view-all/add-requests"
										element={
											<ProtectedRoute
												component={AddRequestsApplications}
												requiredRoles={[
													RoleNames.ADMIN,
													RoleNames.SUPER_ADMIN,
													RoleNames.UNION_LEADER,
												]}
											/>
										}
									/>
									<Route path="*" element={<PageNotFound />} />
								</Routes>
							</div>
						</ToastProvider>
					</PersistGate>
				</Provider>
			</ThemeProvider>
		</Elements>
	);
}

export default App;
